import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '../../config/AppConfigContext';

const joinUrl = (baseUrl: string, path: string) => {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

interface DeleteConversationParams {
  userId: string;
  companyId: string;
  conversationId: string;
}

export const useDeleteConversationMutation = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, companyId, conversationId }: DeleteConversationParams) => {
      if (!allyApi) throw new Error('Missing allyApi configuration');

      const token = auth0Audience
        ? await getAccessTokenSilently({ authorizationParams: { audience: auth0Audience } })
        : await getAccessTokenSilently();

      const params = new URLSearchParams({ companyId });
      const response = await fetch(
        joinUrl(
          allyApi,
          `api/v1/partner/users/${encodeURIComponent(userId)}/conversations/${encodeURIComponent(conversationId)}?${params}`,
        ),
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 204) return;
      if (!response.ok) {
        throw new Error(`Failed to delete conversation: HTTP ${response.status}`);
      }
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['partner', 'users', variables.userId, 'conversations'],
      });
    },
  });
};
